import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Handshake, FileText, Phone, Quote } from "lucide-react";
import api from "@/lib/api";
import VehicleCard from "@/components/VehicleCard";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HERO_IMG = `${BACKEND}/api/static/images/hero-audi.png`;
const TRUST_IMG = `${BACKEND}/api/static/images/trust-vw.png`;

const BRANDS = ["Audi", "BMW", "Mercedes-Benz", "Volkswagen", "Porsche", "Škoda", "Mercedes-AMG", "Audi"];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/vehicles", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden bg-slate-900 grain">
        <img
          src={HERO_IMG}
          alt="D.R Automobile Showroom"
          className="absolute inset-0 w-full h-full object-cover opacity-90 animate-fade-in"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/55 to-slate-900/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-transparent to-transparent" />

        {/* Corner ornament — top-left */}
        <div className="absolute top-8 left-4 md:left-8 lg:left-12 z-10 hidden md:block">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 animate-fade-up">— Est. 2026 · Vöcklabruck</div>
        </div>

        {/* Corner ornament — top-right */}
        <div className="absolute top-8 right-4 md:right-8 lg:right-12 z-10 hidden md:flex items-center gap-3 text-white/60 animate-fade-up">
          <span className="text-[10px] uppercase tracking-[0.3em]">Inhabergeführt</span>
          <span className="block w-12 h-px bg-white/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-[10px] uppercase tracking-[0.25em] px-4 py-2 mb-8 animate-fade-up">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
              Geprüfte Gebrauchtwagen · Österreich
            </div>
            <h1 className="font-heading text-white text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.02] tracking-tight animate-fade-up delay-100">
              Vertrauen.<br />
              Transparenz.<br />
              <span className="font-serif italic font-medium text-red-400">Ihr nächstes Auto.</span>
            </h1>
            <p className="mt-8 text-slate-200 text-base md:text-lg max-w-2xl leading-relaxed animate-fade-up delay-200">
              Bei D.R Automobile finden Sie sorgfältig ausgewählte Gebrauchtwagen mit
              ehrlicher Beratung — vom Inhaber persönlich für Sie.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 animate-fade-up delay-300">
              <Link
                to="/fahrzeuge"
                data-testid="hero-cta-inventory"
                className="group inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-4 font-medium text-sm hover:bg-red-500 hover:text-white transition-crisp"
              >
                Fahrzeuge entdecken
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/kontakt"
                data-testid="hero-cta-contact"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 font-medium text-sm hover:bg-white hover:text-slate-900 transition-crisp"
              >
                Beratung anfragen
              </Link>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="hidden md:block absolute bottom-10 right-12 text-white/60 animate-fade-up delay-500">
            <div className="flex items-center gap-3 -rotate-90 origin-bottom-right">
              <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
              <span className="block w-12 h-px bg-white/40" />
            </div>
          </div>
        </div>
      </section>

      {/* Brand marquee — premium trust signal */}
      <section className="border-y border-slate-200 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-6 flex items-center gap-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 flex-shrink-0 hidden md:block">
            <span className="divider-thin" />Im Bestand
          </div>
          <div className="relative overflow-hidden flex-1">
            <div className="flex gap-12 marquee-track">
              {[...BRANDS, ...BRANDS, ...BRANDS].map((b, i) => (
                <span key={i} className="font-heading font-medium text-xl md:text-2xl text-slate-300 whitespace-nowrap flex-shrink-0">
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST PRINCIPLES — numbered editorial */}
      <section className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-28">
          <div className="grid md:grid-cols-3 gap-2 mb-12">
            <div className="md:col-span-1">
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">
                <span className="divider-thin" />Unser Anspruch
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-slate-900 leading-[1.05]">
                Drei Prinzipien.<br /><span className="font-serif italic font-medium text-red-600">Kompromisslos.</span>
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-10 md:gap-2">
            {[
              { num: "01", icon: ShieldCheck, title: "Geprüfte Qualität", text: "Jedes Fahrzeug vor Übergabe technisch begutachtet. Kein Auto verlässt unseren Bestand ohne sorgfältige Kontrolle." },
              { num: "02", icon: Handshake, title: "Persönliche Beratung", text: "Sie sprechen immer mit dem Inhaber. Keine Hotline, kein Verkaufsdruck — nur ehrliches Gespräch auf Augenhöhe." },
              { num: "03", icon: FileText, title: "Volle Transparenz", text: "Serviceheft, Pickerl, Vorbesitzer, eventuelle Vorschäden — alle Unterlagen liegen offen vor Ihnen." },
            ].map((p, i) => (
              <div key={i} className="md:px-8 border-t md:border-t-0 md:border-l border-slate-200 first:md:border-l-0 pt-8 md:pt-0 md:pl-8 first:md:pl-0">
                <div className="flex items-center gap-4 mb-5">
                  <span className="font-serif italic font-medium text-3xl text-slate-400">{p.num}</span>
                  <p.icon size={22} strokeWidth={1.5} className="text-slate-900" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-slate-900 mb-3">{p.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-28">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">
              <span className="divider-thin" />Aktuelles Angebot
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-slate-900 leading-[1.05]">
              Empfohlene <span className="font-serif italic font-medium">Fahrzeuge</span>
            </h2>
          </div>
          <Link
            to="/fahrzeuge"
            data-testid="home-view-all"
            className="text-sm font-medium text-slate-900 link-underline hover:text-red-600"
          >
            Alle Fahrzeuge ansehen →
          </Link>
        </div>
        {featured.length === 0 ? (
          <p className="text-slate-500 text-sm" data-testid="home-no-featured">Derzeit keine empfohlenen Fahrzeuge.</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {featured.map((v, i) => (
              <div key={v.id} className="animate-fade-up" style={{ animationDelay: `${i * 120}ms` }}>
                <VehicleCard vehicle={v} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* OWNER QUOTE / TRUST */}
      <section className="bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-32 grid md:grid-cols-12 gap-12 md:gap-20 items-center relative">
          <div className="md:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden border border-slate-700 relative">
              <img src={TRUST_IMG} alt="D.R Automobile — Standort" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-red-600 text-white px-6 py-4 hidden md:block">
              <div className="text-[10px] uppercase tracking-[0.3em] opacity-80">Gegründet</div>
              <div className="font-heading font-bold text-3xl">2026</div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-400 mb-6">
              <span className="divider-thin" />Über D.R Automobile
            </div>
            <Quote size={36} strokeWidth={1} className="text-red-500/80 mb-6" />
            <p className="font-serif italic text-2xl md:text-3xl leading-snug text-white">
              „Ich verkaufe keine Autos — ich verkaufe Vertrauen. Jeder Wagen, den Sie hier finden,
              hat meine persönliche Freigabe."
            </p>
            <div className="mt-6 flex items-center gap-4">
              <span className="block w-12 h-px bg-red-500" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400">D. R. · Inhaber</span>
            </div>
            <p className="mt-10 text-slate-300 leading-relaxed">
              Als inhabergeführter Betrieb in Vöcklabruck stehe ich Ihnen vom ersten Kontakt bis
              zur Schlüsselübergabe persönlich zur Seite. Keine wechselnden Verkäufer, keine
              versteckten Klauseln — nur ehrliche Beratung und faire Preise.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="tel:+436677608726"
                data-testid="about-call"
                className="group inline-flex items-center gap-2 bg-white text-slate-900 px-7 py-3.5 font-medium text-sm hover:bg-red-500 hover:text-white transition-crisp"
              >
                <Phone size={16} /> Direkt anrufen
              </a>
              <Link
                to="/ueber-uns"
                data-testid="about-more"
                className="inline-flex items-center gap-2 border border-white/30 px-7 py-3.5 font-medium text-sm hover:bg-white hover:text-slate-900 transition-crisp"
              >
                Mehr erfahren
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-28">
        <div className="border-y border-slate-900 py-16 md:py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">
              <span className="divider-thin" />Individuelle Suche
            </div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-slate-900 leading-[1.05]">
              Nicht das Passende dabei?
            </h2>
            <p className="text-slate-600 mt-4 max-w-xl">
              Sagen Sie mir, was Sie suchen — ich finde das passende Fahrzeug für Sie.
            </p>
          </div>
          <Link
            to="/kontakt"
            data-testid="cta-contact"
            className="group inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 font-medium text-sm hover:bg-red-500 transition-crisp flex-shrink-0"
          >
            Anfrage senden <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
